import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRight, Home as HomeIcon, MapPin, Clock, User, Share2, Facebook, Twitter, Linkedin, Link2, ExternalLink } from 'lucide-react';
import ArticleCard from '../components/ArticleCard';
import Sidebar from '../components/Sidebar';
import AdBox from '../components/AdBox';
import { fetchArticleById, fetchWeather, fetchTrending } from '../services/newsService';
import { AD_SLOTS, timeAgo } from '../mock/mock';
import { fallbackImage } from '../lib/images';

const CAT_LABEL = { headlines: 'Headline News', politics: 'Politics', sport: 'Sport', financials: 'Financials', fashion: 'Fashion' };

export default function Article() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [weather, setWeather] = useState([]);
  const [trending, setTrending] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let m = true;
    setLoading(true);
    Promise.all([fetchArticleById(id), fetchWeather(), fetchTrending()]).then(([d, w, t]) => {
      if (!m) return;
      setData(d); setWeather(w); setTrending(t); setLoading(false);
    });
    return () => { m = false; };
  }, [id]);

  if (loading) {
    return <div className="max-w-[1320px] mx-auto px-4 py-16 text-center"><div className="inline-block w-8 h-8 border-2 border-[#157347] border-t-transparent rounded-full animate-spin" /></div>;
  }
  if (!data || !data.article) {
    return (
      <div className="max-w-[1320px] mx-auto px-4 py-24 text-center">
        <h1 className="font-display text-3xl text-[#0b3d26] font-bold">Story not found</h1>
        <Link to="/" className="inline-block mt-4 text-[#157347] font-semibold">Return to home</Link>
      </div>
    );
  }

  const { article: a, related } = data;

  return (
    <div className="max-w-[1320px] mx-auto px-4 py-6">
      <nav className="flex items-center gap-1.5 text-[13px] text-[#5b6b63] mb-4 flex-wrap">
        <Link to="/" className="inline-flex items-center gap-1 hover:text-[#157347]"><HomeIcon className="w-3.5 h-3.5" />Home</Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <Link to={`/category/${a.category}`} className="hover:text-[#157347]">{CAT_LABEL[a.category]}</Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="text-[#157347] font-semibold truncate max-w-[200px]">{a.title}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <article className="lg:col-span-2">
          <span className="inline-block text-[11px] font-bold uppercase tracking-wider font-cond text-white bg-[#157347] px-2.5 py-1 rounded">{CAT_LABEL[a.category]}</span>
          <h1 className="font-display font-extrabold text-3xl sm:text-[42px] leading-tight text-[#0b3d26] mt-3">{a.title}</h1>
          <p className="text-lg text-[#3c4a43] mt-3 leading-relaxed">{a.excerpt}</p>

          <div className="flex flex-wrap items-center gap-4 mt-4 pb-4 border-b border-[#e3ece7] text-[13px] text-[#5b6b63]">
            <span className="inline-flex items-center gap-1.5"><User className="w-4 h-4 text-[#157347]" />By {a.author}</span>
            <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[#157347]" />{a.city}, MI</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4 text-[#157347]" />{timeAgo(a.publishedAt)} · {a.readTime} min read</span>
            <span className="ml-auto inline-flex items-center gap-2">
              <Share2 className="w-4 h-4" />
              {[Facebook, Twitter, Linkedin, Link2].map((Icon, i) => (
                <span key={i} className="w-8 h-8 rounded-full bg-[#eef8f2] hover:bg-[#d8f3e3] flex items-center justify-center cursor-pointer transition-colors"><Icon className="w-4 h-4 text-[#157347]" /></span>
              ))}
            </span>
          </div>

          <div className="img-zoom rounded-xl overflow-hidden mt-5 shadow-sm">
            <img src={a.image} alt={a.title} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = fallbackImage(a.category, a.id); }} className="w-full h-auto max-h-[520px] object-cover" />
          </div>
          <p className="text-xs text-[#9aa8a1] mt-2 italic">Source: {a.source}</p>

          <div className="mt-6">
            <p className="text-[17px] leading-[1.85] text-[#26332d]">
              This story is aggregated by TMB Global News from <span className="font-semibold">{a.source}</span>. Read the complete report, including full details and the latest updates, at the original source.
            </p>
            <a href={a.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-5 bg-[#157347] hover:bg-[#0f5132] text-white font-semibold px-5 py-3 rounded-lg transition-colors">
              Read full story at {a.source} <ExternalLink className="w-4 h-4" />
            </a>
            <div className="my-8"><AdBox size={AD_SLOTS.inline.size} label="Your Ad Here" note="In-article placement" height={110} /></div>
          </div>

          {related.length > 0 && (
            <div className="mt-10">
              <h3 className="font-display font-bold text-2xl text-[#0b3d26] border-b-2 border-[#157347] pb-2 mb-5">Related Stories</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {related.map((r) => (<ArticleCard key={r.id} article={r} variant="feature" />))}
              </div>
            </div>
          )}
        </article>

        <div className="lg:col-span-1">
          <Sidebar weather={weather} trending={trending} ads={AD_SLOTS.sidebar} />
        </div>
      </div>
    </div>
  );
}
